/**
 * Laabha — typed API client for the chat BLL surface.
 *
 * Talks to:
 *   /api/v1/chat/conversations*        (chat_bll.py — secretary proxy)
 *   /api/v1/chat/{de-catalog,invoke}   (chat_invoke_bll.py — chat-bll service)
 *
 * Auth: relies on the browser session cookie or Authorization header set by
 * the shell. In dev (vite proxy), credentials are forwarded automatically.
 */

import type {
  Conversation,
  Envelope,
  Message,
  DigitalEmployee,
  InvokeRequest,
  InvokeResponse,
} from '@/types/chat';
import { authHeader } from './auth';
import {
  deleteLocalConversation,
  getCachedMessages,
  listLocalConversations,
  mergeConversations,
  mergeMessages,
  saveLocalConversation,
} from './conversationCache';

// Gateway mounts the chat BLL at /v1/chat (NOT /api/v1/chat). The /api prefix
// only exists for L1-passthrough routes like /api/v1/aiaccountant/*.
const BASE = '/v1/chat';

class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

async function unwrap<T>(resp: Response): Promise<T> {
  if (resp.status === 401) {
    // 401 here may be a TRANSIENT failure (BLL hiccup, race during login)
    // or genuine token-expiry. We DO NOT clearAuth() unconditionally because
    // that would cascade across parallel queries and kick the user back to
    // login on any momentary blip. Instead, schedule a single local JWT check;
    // only a genuinely expired + unrefreshable token is allowed to wipe the session.
    void verifyTokenSoon();
    throw new ApiError('AUTH_REQUIRED', 'Authorization failed for this request.', 401);
  }
  if (!resp.ok && resp.status !== 422) {
    throw new ApiError(`HTTP_${resp.status}`, `${resp.status} ${resp.statusText}`, resp.status);
  }
  const body = (await resp.json()) as Envelope<T>;
  if (!body.success) {
    const code = body.error?.code ?? 'UNKNOWN';
    const msg = body.error?.message ?? 'Request failed';
    throw new ApiError(code, msg, resp.status);
  }
  if (body.data === null) {
    throw new ApiError('EMPTY_DATA', 'Envelope succeeded but data is null', resp.status);
  }
  return body.data;
}

// ── Token-verify debounce ────────────────────────────────────────────────
// When a 401 fires, schedule a single /auth/me check. If THAT fails, clear
// the token (legit expiry). Multiple parallel 401s collapse to one verify.
let verifyScheduled = false;
async function verifyTokenSoon() {
  if (verifyScheduled) return;
  verifyScheduled = true;
  try {
    // Wait one tick so a burst of parallel 401s collapses to a single check.
    await new Promise((r) => setTimeout(r, 50));
    const { isJwtValid, refreshZitadelToken, clearAuth } = await import('./auth');
    if (isJwtValid()) {
      // JWT is locally valid — the 401 was a transient server issue; leave the user alone.
      // (Gateway Zitadel credentials may not be configured; that's a server issue, not ours.)
      return;
    }
    // JWT is expired — attempt a silent Zitadel refresh before giving up.
    const refreshed = await refreshZitadelToken();
    if (!refreshed) {
      // Refresh also failed — token is genuinely dead. Force re-login.
      clearAuth();
    }
  } finally {
    verifyScheduled = false;
  }
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...authHeader(),
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  return unwrap<T>(resp);
}

// ── Conversations ──────────────────────────────────────────────────────────

/**
 * The BLL (chat_bll.py) returns conversations as a **flat shape**, NOT wrapped:
 *
 *   POST /conversations           → data: { id, title, created_at, messages: [] }
 *   GET  /conversations           → data: [conv, conv, …]   (array directly, or [] fallback)
 *   GET  /conversations/{id}/messages → data: { messages: […] }   (wrapped)
 *
 * Match the actual contract — don't expect a wrapper key that doesn't exist.
 */

interface MessagesResponse {
  messages: Message[];
  /** Some secretary versions include the parent conversation; tolerate. */
  conversation?: Conversation;
}

/** Normalise list response: BLL may return [] OR { conversations: [...] } OR
 *  the secretary's native shape with `data: [...]`. Handle all three. */
function normaliseList(raw: unknown): Conversation[] {
  if (Array.isArray(raw)) return raw as Conversation[];
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>;
    if (Array.isArray(r.conversations)) return r.conversations as Conversation[];
    if (Array.isArray(r.data)) return r.data as Conversation[];
  }
  return [];
}

export const conversationsApi = {
  /**
   * Returns the union of secretary-tracked conversations and our local
   * client-side cache of AIAccountant-only conversations. Local entries are
   * the ones secretary doesn't know about (because we deliberately avoid
   * routing import/report turns through secretary's agent loop).
   */
  list: async (): Promise<{ conversations: Conversation[] }> => {
    let server: Conversation[] = [];
    try {
      const raw = await jsonFetch<unknown>('/conversations');
      server = normaliseList(raw);
    } catch {
      // Tolerate transient gateway issues — local cache is still useful
    }
    return {
      conversations: mergeConversations(server, listLocalConversations()),
    };
  },

  create: async (title?: string): Promise<{ conversation: Conversation }> => {
    const data = await jsonFetch<Conversation>('/conversations', {
      method: 'POST',
      body: JSON.stringify({ title: title ?? 'New Conversation' }),
    });
    // Save in local cache immediately so it survives reloads even if no
    // messages get sent yet.
    saveLocalConversation(data);
    return { conversation: data };
  },

  /**
   * Returns the merged transcript — server-tracked messages from secretary
   * (free-form chat turns) PLUS locally-cached messages from AIAccountant
   * flows. Both sets are deduped by id and ordered by created_at.
   */
  getMessages: async (id: string): Promise<{ messages: Message[] }> => {
    let server: Message[] = [];
    try {
      const data = await jsonFetch<MessagesResponse>(`/conversations/${id}/messages`);
      server = data.messages ?? [];
    } catch {
      // Server may 404 for AIAccountant-only conversations — fine, use cache only
    }
    return { messages: mergeMessages(server, getCachedMessages(id)) };
  },

  /** Non-streaming send (use stream() for real-time tokens). */
  sendMessage: async (id: string, message: string): Promise<{ message: Message }> => {
    const data = await jsonFetch<Message | { message: Message }>(
      `/conversations/${id}/message`,
      {
        method: 'POST',
        body: JSON.stringify({ message }),
      },
    );
    const msg = (data as { message?: Message }).message ?? (data as Message);
    return { message: msg };
  },

  delete: async (id: string): Promise<{ deleted: boolean }> => {
    try {
      await jsonFetch<unknown>(`/conversations/${id}`, { method: 'DELETE' });
    } catch {
      // Server may 404 (local-only conversation) — still purge cache
    }
    deleteLocalConversation(id);
    return { deleted: true };
  },
};

// ── Digital Employee invocation ────────────────────────────────────────────

/**
 * Friendly display overrides for known slash commands. The gateway returns
 * Lucide icon NAMES in `manifest.icon` (e.g. "tally", "users") which we can't
 * render directly in a <select> option — map them to emojis here and reword
 * the labels to Laabha conventions (AIAccountant focus).
 *
 * If a slash isn't in this map, we fall back to the manifest values.
 */
const DE_DISPLAY_OVERRIDES: Record<string, { icon: string; label: string; rank: number }> = {
  '/tally-import':     { icon: '🧾', label: 'AI Accountant — PDF/Excel to Tally', rank: 0 },
  '/financial-report': { icon: '💰', label: 'AI Accountant — Financial Report',   rank: 1 },
  '/ar-followup-in':   { icon: '₹',  label: 'AR Follow-up (India)',                rank: 5 },
  '/ar-followup-nz':   { icon: '$',  label: 'AR Follow-up (NZ)',                   rank: 6 },
  '/bank-rec-xero':    { icon: '🏦', label: 'Bank Reconciliation (Xero)',          rank: 7 },
  '/cs-india':         { icon: '🎧', label: 'Customer Support (India)',            rank: 9 },
  '/payroll-summary':  { icon: '👥', label: 'Payroll Summary (MYOB)',              rank: 8 },
  '/sales-indiamart':  { icon: '💼', label: 'Sales Rep (IndiaMART)',               rank: 10 },
  '/receptionist-nz':  { icon: '📞', label: 'Reception Desk (NZ)',                 rank: 11 },
  '/export-ops-nz':    { icon: '📦', label: 'Export Operations (NZ)',              rank: 12 },
  '/property-inventory': { icon: '🏠', label: 'Property Inventory PDF',            rank: 13 },
};

/**
 * Normalise the de-catalog response. The gateway returns the array directly
 * (i.e. `data: [...]`), but older code may return `{ bindings: [...] }`.
 * We accept both and derive the convenience fields (display_name, icon, …)
 * so component code doesn't have to special-case the nested manifest.
 *
 * Also sorts AIAccountant DEs to the top via the DE_DISPLAY_OVERRIDES rank.
 */
function normaliseDes(raw: unknown): DigitalEmployee[] {
  let arr: unknown[] = [];
  if (Array.isArray(raw)) arr = raw;
  else if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>;
    if (Array.isArray(r.bindings)) arr = r.bindings as unknown[];
    else if (Array.isArray(r.des)) arr = r.des as unknown[];
    else if (Array.isArray(r.data)) arr = r.data as unknown[];
  }
  const mapped = arr.map((b) => {
    const x = b as DigitalEmployee;
    const m = x.manifest ?? {};
    const override = DE_DISPLAY_OVERRIDES[x.slash];
    return {
      ...x,
      display_name: override?.label ?? m.name ?? x.de_name ?? x.slash,
      icon: override?.icon ?? (looksLikeEmoji(m.icon) ? m.icon : '🔧'),
      description: m.description_md,
      sample_prompts: (m.examples ?? []).map((e) => e.user_input ?? '').filter(Boolean),
    };
  });
  // Sort by override rank (AIAccountant entries first), then alphabetically.
  return mapped.sort((a, b) => {
    const ra = DE_DISPLAY_OVERRIDES[a.slash]?.rank ?? 999;
    const rb = DE_DISPLAY_OVERRIDES[b.slash]?.rank ?? 999;
    if (ra !== rb) return ra - rb;
    return (a.display_name ?? '').localeCompare(b.display_name ?? '');
  });
}

/** Cheap heuristic: a 1-2 char string is probably an emoji, longer is a Lucide name. */
function looksLikeEmoji(s: string | undefined): boolean {
  if (!s) return false;
  return s.length <= 4 && /\p{Extended_Pictographic}/u.test(s);
}

export const deApi = {
  catalog: async (): Promise<{ bindings: DigitalEmployee[] }> => {
    const raw = await jsonFetch<unknown>('/de-catalog');
    return { bindings: normaliseDes(raw) };
  },

  invoke: (req: InvokeRequest) =>
    jsonFetch<InvokeResponse>('/invoke', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  audit: (limit = 50) =>
    jsonFetch<{ audit: unknown[] }>(`/audit?limit=${limit}`),
};

export { ApiError };
